import React, { useState, useContext, useEffect } from 'react';
import { AccountContext } from '../../components/AccountContext';
import { Panel } from 'primereact/panel';
import { defineMessages, useIntl } from 'react-intl';
import gql from 'graphql-tag';
import moment from 'moment-timezone';
import { NotificationContext } from '../../components/GrowlNotifications';
import { Loading } from '../../components/Loading';
import { growlErrors } from '../../utils/NotificationUtil';
import { useQuery } from '@apollo/react-hooks';
import { AppConstants } from '../../shared/Constants';
import { getNumberOfEmployeesLabel, buildSubscriptionPeriodText } from '../../utils/AppUtil';
import Invoice from '../../components/Invoice';

const INVOICES_QUERY = gql`
  query invoicesQuery($accountId: ID!) {
    account(id: $accountId) {
      id
      invoicesPaidForSelf {
        id
        invoiceId
        invoiceNumber
        paymentDate
        paymentAmount
        subscriptionStartDate
        subscriptionEndDate
        numberOfEmployees
        subscriptionPeriodDuration
      }
    }
  }
`;

const i18nMessages = defineMessages({
  invoices: {
    id: 'menu.invoices',
    defaultMessage: 'Invoices',
  },
  invoice: { id: 'account.invoices.invoice', defaultMessage: 'Invoice' },
  paid: { id: 'account.invoices.paid', defaultMessage: 'Paid' },
  amount: { id: 'account.invoices.amount', defaultMessage: 'Amount' },
  startDate: { id: 'account.invoices.start-date', defaultMessage: 'Start date' },
  period: { id: 'account.invoices.period', defaultMessage: 'Period' },
  renewalDate: { id: 'account.invoices.renewal-date', defaultMessage: 'Renewal date' },
  internalRespondents: { id: 'account.invoices.internal-respondents', defaultMessage: 'Int. Resp.' },
  noInvoices: {
    id: 'account.invoices.no-invoices',
    defaultMessage: 'There are currently no invoices',
  },
  month: {
    id: 'account.subscription.month',
    defaultMessage: 'month',
  },
  months: {
    id: 'account.subscription.months',
    defaultMessage: 'months',
  },
});

const Invoices = () => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();

  const [invoices, setInvoices] = useState([]);

  const { loading, error, data } = useQuery(INVOICES_QUERY, {
    variables: {
      accountId: currentAccount,
    },
    fetchPolicy: 'no-cache',
  });

  const { account } = data;

  useEffect(() => {
    if (account) {
      setInvoices(account.invoicesPaidForSelf);
    }
    // eslint-disable-next-line
  }, [account]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  return (
    <>
      <div className="p-grid">
        <div className="p-col-12">
          <div className="brand-detailed-wrapper">
            <Panel header={formatMessage(i18nMessages.invoices)}>
              {invoices.length === 0 && (
                <div className="align-center font-weight-600">{formatMessage(i18nMessages.noInvoices)}</div>
              )}
              {invoices.length > 0 && (
                <>
                  <div className="font-weight-600 flex-space-between">
                    <div>{formatMessage(i18nMessages.invoice)}</div>
                    <div>{formatMessage(i18nMessages.paid)}</div>
                    <div>{formatMessage(i18nMessages.amount)}</div>
                    <div>{formatMessage(i18nMessages.startDate)}</div>
                    <div>{formatMessage(i18nMessages.period)}</div>
                    <div>{formatMessage(i18nMessages.renewalDate)}</div>
                    <div>{formatMessage(i18nMessages.internalRespondents)}</div>
                  </div>
                  <>
                    {invoices.map((invoice) => {
                      return (
                        <div key={invoice.id} className="flex-space-between">
                          <Invoice
                            managingAccountId={account.id}
                            clientAccountId={account.id}
                            invoiceId={invoice.invoiceId}
                            invoiceNumber={invoice.invoiceNumber}
                          />
                          <div>{moment(invoice.paymentDate).format(AppConstants.DATE_INPUT_FORMAT)}</div>
                          <div>${invoice.paymentAmount.toLocaleString()}</div>
                          <div>{moment(invoice.subscriptionStartDate).format(AppConstants.DATE_INPUT_FORMAT)}</div>
                          <div>
                            {buildSubscriptionPeriodText(
                              formatMessage(i18nMessages.months),
                              formatMessage(i18nMessages.month),
                              invoice.subscriptionPeriodDuration,
                            )}
                          </div>
                          <div>{moment(invoice.subscriptionEndDate).format(AppConstants.DATE_INPUT_FORMAT)}</div>
                          <div>{getNumberOfEmployeesLabel(invoice.numberOfEmployees)}</div>
                        </div>
                      );
                    })}
                  </>
                </>
              )}
            </Panel>
          </div>
        </div>
      </div>
    </>
  );
};

export default Invoices;
